import React from "react";
import { useNavigate } from "react-router-dom";
import { ImageClose, TickIcon } from "../../assets/icons";
import CommentBadge from "./CommentBadge";

const ProgressPhotos = ({
  photos,
  singlePhoto = false,
  progress = false,
  selectedPhotos = [],
  handlePhotoSelect,
  handleRemovePhoto,
  onPhotosAvailable,
}) => {
  const navigate = useNavigate();

  const getFileType = (url) => {
    if (!url || typeof url !== 'string') return 'other';
    const extension = url.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return 'image';
    } else if (['mp4', 'webm', 'ogg', 'mov'].includes(extension)) {
      return 'video';
    } else if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(extension)) {
      return 'audio';
    }
    return 'other';
  };

  const renderMedia = (media, size) => {
    const fileType = getFileType(media.url);

    if (fileType === 'image') {
      return (
        <img
          src={media.url}
          alt={`Progress ${media.id}`}
          className={`object-cover rounded-[4px] ${size} cursor-pointer`}
          onClick={() =>
            progress
              ? handlePhotoSelect(media)
              : navigate("/single-photo", { state: { photo: media } })
          }
        />
      );
    } else if (fileType === 'video') {
      return (
        <video
          controls
          className={`object-contain rounded-[4px] ${size} bg-black cursor-pointer max-h-[360px]`}
          onClick={() =>
            progress
              ? handlePhotoSelect(media)
              : navigate("/single-photo", { state: { photo: media } })
          }
        >
          <source src={media.url} />
          Your browser does not support the video tag.
        </video>
      );
    } else if (fileType === 'audio') {
      return (
        <div  onClick={() =>
          progress
            ? handlePhotoSelect(media)
            : navigate("/single-photo", { state: { photo: media } })
        } className={`${size} bg-gray-100 rounded-[4px] flex items-center justify-center p-4`}>
          <audio  controls className="w-full max-w-md">
            <source src={media.url} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }
    return (
      <div className={`${size} bg-gray-100 rounded-[4px] flex items-center justify-center p-2`}>
        <a 
          href={media.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {media.url.split('/').pop()}
        </a>
      </div>
    );
  };

  const isPhotoSelected = (photo) =>
    selectedPhotos.some((selected) => selected.id === photo.id);

  const visiblePhotos = singlePhoto ? photos : photos?.slice(0, 15);

  React.useEffect(() => {
    if (onPhotosAvailable) {
      onPhotosAvailable(!!(photos && photos.length > 0));
    }
  }, [photos, onPhotosAvailable]);

  if (!photos || photos.length === 0) {
    return (
      <div className="mt-6 text-center text-gray-500">
        No progress data available.
      </div>
    );
  }

  return (
    <div className={`mt-6 ${progress ? "flex flex-col gap-4" : "flex"} w-full`}>
      <div className={`w-full overflow-x-auto`} style={{ maxWidth: "100%" }}>
        <div
          className={`grid ${
            singlePhoto ? "grid-rows-1 !w-full" : "grid-rows-1"
          } grid-flow-col gap-3`}
          style={{ width: "max-content" }}
        >
          {visiblePhotos?.map((media, index) => (
            <div key={index} className="relative">
              {renderMedia(
                media,
                singlePhoto ? "w-full h-[343px]" : "w-[120px] h-[98px]"
              )}
              {isPhotoSelected(media) && (
                <span className="absolute top-1 right-1">{TickIcon}</span>
              )}
              {media.commentCount > 0 && (
                <CommentBadge count={media.commentCount} />
              )}
            </div>
          ))}
          {photos?.slice(15)?.map((media, index) => (
            <div key={`extra-${index}`} className="relative">
              {renderMedia(
                media,
                singlePhoto ? "w-full h-[343px]" : "w-[155px] h-[98px]"
              )}
              {isPhotoSelected(media) && (
                <span className="absolute top-1 right-1">{TickIcon}</span>
              )}
              {media.commentCount > 0 && (
                <CommentBadge count={media.commentCount} />
              )}
            </div>
          ))}
        </div>
      </div>
      {progress && selectedPhotos?.length > 0 && (
        <div>
          <h3 className="font-semibold text-[14px] text-black mb-2">
            Selected Progress Media
          </h3>
          <div className="gap-2 grid grid-cols-12 mt-4">
            {selectedPhotos.map((media, index) => (
              <div key={index} className="relative">
                {renderMedia(media, "w-auto h-[70px]")}
                <span
                  className="absolute top-[-10px] right-[-10px] rounded-full p-1 cursor-pointer"
                  onClick={() => handleRemovePhoto(media)}
                >
                  {ImageClose}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressPhotos;
